import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  DEFAULT_SEARCH_WINDOW_DAYS,
  useLostSales,
} from "../../context/lostSalesContext";
import { LOST_SALES_SAFE_PERIOD_OPTIONS } from "../../utils/lostSalesParams";

const productLabel = (product) => {
  if (!product) return "Not available";
  if (typeof product === "string") return product;
  return product.name || product.sku || "Not available";
};

const warehouseLabel = (warehouse) => {
  if (!warehouse) return "Not available";
  if (typeof warehouse === "string") return warehouse;
  return (
    warehouse.warehouse_id ||
    warehouse.warehouse_code ||
    warehouse.name ||
    "Not available"
  );
};

const parseSearchWindowInput = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return { status: "blank" };
  if (!/^\d+$/.test(trimmed)) return { status: "invalid" };
  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed) || parsed <= 0) return { status: "invalid" };
  return { status: "valid", value: parsed };
};

const LostSalesParamsModal = () => {
  const {
    paramsDialogOpen,
    setParamsDialogOpen,
    selectedInventory,
    selectedInventories,
    bulkScopes,
    analysisLevel,
    selectionError,
    isBulkMode,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchWindowDays,
    setSearchWindowDays,
    demandWindowDays,
    setDemandWindowDays,
    dowAdjustmentEnabled,
    setDowAdjustmentEnabled,
    error,
    clearError,
    submitting,
    submitLostSales,
  } = useLostSales();
  const [searchWindowInput, setSearchWindowInput] = useState(
    String(searchWindowDays ?? DEFAULT_SEARCH_WINDOW_DAYS),
  );

  useEffect(() => {
    if (!paramsDialogOpen) return;
    setSearchWindowInput(
      Number.isInteger(searchWindowDays) && searchWindowDays > 0
        ? String(searchWindowDays)
        : String(DEFAULT_SEARCH_WINDOW_DAYS),
    );
    // Sync only when the dialog opens so clearing the field while typing is not reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsDialogOpen]);

  const searchWindowParse = parseSearchWindowInput(searchWindowInput);
  const effectiveSearchWindow =
    searchWindowParse.status === "valid"
      ? searchWindowParse.value
      : DEFAULT_SEARCH_WINDOW_DAYS;
  const invalidRange = !startDate || !endDate || startDate > endDate;
  const invalidSearchWindowInput = searchWindowParse.status === "invalid";
  const nearestSafePeriodActive = demandWindowDays != null;
  const invalidDemandWindow =
    nearestSafePeriodActive &&
    (!Number.isInteger(demandWindowDays) || demandWindowDays <= 0);
  const demandExceedsSearch =
    nearestSafePeriodActive &&
    searchWindowParse.status !== "invalid" &&
    demandWindowDays > effectiveSearchWindow;
  const invalidParams =
    invalidSearchWindowInput || invalidDemandWindow || demandExceedsSearch;
  const unresolvedBulkGrain =
    isBulkMode && analysisLevel !== "product" && analysisLevel !== "sku";
  const cannotSubmit =
    Boolean(selectionError) ||
    invalidRange ||
    invalidParams ||
    unresolvedBulkGrain;

  const warehouse =
    selectedInventory?.warehouse || selectedInventories[0]?.warehouse;
  const scopeCount = isBulkMode
    ? bulkScopes.length || selectedInventories.length
    : 1;

  return (
    <Dialog
      open={paramsDialogOpen}
      onClose={() => !submitting && setParamsDialogOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 2, position: "relative", overflow: "hidden" },
      }}
    >
      {submitting && (
        <LinearProgress
          sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
        />
      )}
      <DialogTitle sx={{ fontFamily: "Poppins", fontWeight: 600, pb: 1 }}>
        Lost Sales Parameters
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
          Estimate sales potentially missed due to insufficient inventory.
        </Alert>
        {selectionError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {selectionError}
          </Alert>
        )}
        {submitting && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.75,
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <CircularProgress size={26} thickness={4} />
            <Box>
              <Typography
                sx={{ fontFamily: "Poppins", fontWeight: 600, fontSize: 14 }}
              >
                Analyzing data...
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", fontFamily: "Poppins" }}
              >
                {isBulkMode
                  ? `Running analysis for ${scopeCount} scope${scopeCount === 1 ? "" : "s"}.`
                  : "This may take a few moments."}
              </Typography>
            </Box>
          </Box>
        )}
        <Box
          sx={{
            opacity: submitting ? 0.55 : 1,
            pointerEvents: submitting ? "none" : "auto",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gap: 1.25,
              mb: 3,
              p: 2,
              borderRadius: 2,
              bgcolor: "var(--bg-highlight, rgba(0,0,0,0.03))",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "text.secondary",
              }}
            >
              Analysis context
            </Typography>
            {!isBulkMode ? (
              <>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>Product:</strong>{" "}
                  {productLabel(selectedInventory?.product)}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>SKU:</strong>{" "}
                  {selectedInventory?.sku || "Not available"}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>Warehouse:</strong> {warehouseLabel(warehouse)}
                </Typography>
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>Mode:</strong>{" "}
                  {analysisLevel === "product"
                    ? "Product-level (child SKUs pooled)"
                    : "SKU-level"}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>Scopes:</strong> {scopeCount}
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "Poppins" }}>
                  <strong>Warehouse:</strong> {warehouseLabel(warehouse)}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                  {(bulkScopes.length
                    ? bulkScopes
                    : selectedInventories
                  ).slice(0, 12).map((scope) => (
                    <Chip
                      key={scope.key || `${toChipKey(scope)}`}
                      size="small"
                      label={
                        analysisLevel === "product"
                          ? productLabel(scope.product)
                          : scope.sku || productLabel(scope.product)
                      }
                    />
                  ))}
                  {scopeCount > 12 && (
                    <Chip size="small" label={`+${scopeCount - 12} more`} />
                  )}
                </Box>
              </>
            )}
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "grid", gap: 2 }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(event) => {
                clearError();
                setStartDate(event.target.value);
              }}
              disabled={submitting}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(event) => {
                clearError();
                setEndDate(event.target.value);
              }}
              disabled={submitting}
              InputLabelProps={{ shrink: true }}
              fullWidth
              error={invalidRange}
              helperText={
                invalidRange ? "End Date must be on or after Start Date." : ""
              }
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: "grid", gap: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: "Poppins",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                color: "text.secondary",
              }}
            >
              Calculation settings
            </Typography>
            <TextField
              label="Search Window (days)"
              type="text"
              inputMode="numeric"
              value={searchWindowInput}
              onChange={(event) => {
                const next = event.target.value;
                clearError();
                setSearchWindowInput(next);
                const parsed = parseSearchWindowInput(next);
                if (parsed.status === "blank") setSearchWindowDays(null);
                else if (parsed.status === "valid")
                  setSearchWindowDays(parsed.value);
                else setSearchWindowDays(0);
              }}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={submitting}
              error={invalidSearchWindowInput}
              helperText={
                invalidSearchWindowInput
                  ? "Enter a positive whole number of days."
                  : searchWindowParse.status === "blank"
                    ? `Leave blank to use the default: ${DEFAULT_SEARCH_WINDOW_DAYS} days.`
                    : ""
              }
            />
            <FormControl
              fullWidth
              disabled={submitting}
              error={invalidDemandWindow || demandExceedsSearch}
            >
              <InputLabel id="nearest-safe-period-label" shrink>
                Nearest Safe Period
              </InputLabel>
              <Select
                labelId="nearest-safe-period-label"
                label="Nearest Safe Period"
                value={demandWindowDays ?? ""}
                displayEmpty
                onChange={(event) => {
                  clearError();
                  const { value } = event.target;
                  setDemandWindowDays(value === "" ? null : Number(value));
                }}
              >
                <MenuItem value="">Inactive</MenuItem>
                {LOST_SALES_SAFE_PERIOD_OPTIONS.map((days) => (
                  <MenuItem key={days} value={days}>
                    {days} days
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {invalidDemandWindow
                  ? "Choose Inactive or a positive whole number of days."
                  : demandExceedsSearch
                    ? "Must not exceed the search window."
                    : nearestSafePeriodActive
                      ? "Find a safe period of N days within the search window."
                      : "Use the search window and exclude stockout days."}
              </FormHelperText>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={dowAdjustmentEnabled}
                  disabled={submitting}
                  onChange={(event) => {
                    clearError();
                    setDowAdjustmentEnabled(event.target.checked);
                  }}
                />
              }
              label="DOW Adjustment"
              sx={{ fontFamily: "Poppins", ml: 0 }}
            />
          </Box>
        </Box>
        {error && !submitting && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography
              sx={{ fontFamily: "Poppins", fontWeight: 600, mb: 0.5 }}
            >
              {error.title}
            </Typography>
            <Typography sx={{ fontFamily: "Poppins", fontSize: 14 }}>
              {error.body}
            </Typography>
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          disabled={submitting}
          onClick={() => setParamsDialogOpen(false)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!submitting && cannotSubmit}
          onClick={() => {
            if (submitting || cannotSubmit) return;
            submitLostSales();
          }}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" thickness={5} />
            ) : null
          }
        >
          {submitting ? "Analyzing..." : "Run Analysis"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const toChipKey = (scope) =>
  `${scope.productId || scope.product?._id || ""}:${scope.sku || ""}`;

export default LostSalesParamsModal;
